/**
 * Work Item Timeline - Chronological view of work items
 *
 * Groups items by due date:
 * - Retard (overdue)
 * - Aujourd'hui (today)
 * - Demain (tomorrow)
 * - Cette semaine (this week)
 * - Plus tard (later)
 * - Sans échéance (no due date)
 */

import React, { useMemo } from 'react';
import { Calendar, AlertTriangle, Clock, CalendarDays, CalendarRange, Inbox } from 'lucide-react';
import WorkItemRow from './WorkItemRow';

// Timeline group definitions
const TIMELINE_GROUPS = {
  overdue: {
    key: 'overdue',
    label: 'En retard',
    icon: AlertTriangle,
    color: 'red',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700'
  },
  today: {
    key: 'today',
    label: "Aujourd'hui",
    icon: Clock,
    color: 'amber',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700'
  },
  tomorrow: {
    key: 'tomorrow',
    label: 'Demain',
    icon: Calendar,
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700'
  },
  thisWeek: {
    key: 'thisWeek',
    label: 'Cette semaine',
    icon: CalendarDays,
    color: 'indigo',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-700'
  },
  later: {
    key: 'later',
    label: 'Plus tard',
    icon: CalendarRange,
    color: 'gray',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-600'
  },
  noDueDate: {
    key: 'noDueDate',
    label: 'Sans échéance',
    icon: Inbox,
    color: 'slate',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    textColor: 'text-slate-500'
  }
};

/**
 * Categorize a date into a timeline group
 */
function getTimelineGroup(dueDateStr) {
  if (!dueDateStr) return 'noDueDate';

  const dueDate = new Date(dueDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - today.getDay()));

  if (dueDate < today) return 'overdue';
  if (dueDate.toDateString() === today.toDateString()) return 'today';
  if (dueDate.toDateString() === tomorrow.toDateString()) return 'tomorrow';
  if (dueDate <= endOfWeek) return 'thisWeek';
  return 'later';
}

/**
 * Group items by timeline
 */
function groupItemsByTimeline(items) {
  const groups = {
    overdue: [],
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
    noDueDate: []
  };

  for (const item of items) {
    const group = getTimelineGroup(item.due_date);
    groups[group].push(item);
  }

  // Sort items within each group by priority (P0 first) then due_date
  const priorityOrder = { P0: 0, P1: 1, P2: 2, P3: 3 };
  Object.keys(groups).forEach(key => {
    groups[key].sort((a, b) => {
      const priorityDiff = (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
      if (priorityDiff !== 0) return priorityDiff;
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    });
  });

  return groups;
}

/**
 * Timeline Group Header
 */
function TimelineGroupHeader({ group, count }) {
  const config = TIMELINE_GROUPS[group];
  const Icon = config.icon;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${config.bgColor} border-b ${config.borderColor}`}>
      <div className={`p-1.5 rounded-lg bg-white shadow-sm`}>
        <Icon className={`w-4 h-4 ${config.textColor}`} />
      </div>
      <h3 className={`font-medium ${config.textColor}`}>
        {config.label}
      </h3>
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-white ${config.textColor}`}>
        {count}
      </span>
    </div>
  );
}

/**
 * WorkItemTimeline component
 */
function WorkItemTimeline({ items = [], onAction, onNavigate, onEmail, onSuggestedAction }) {
  const groupedItems = useMemo(() => groupItemsByTimeline(items), [items]);

  // Get groups that have items
  const activeGroups = Object.entries(groupedItems)
    .filter(([, groupItems]) => groupItems.length > 0);

  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <p className="text-gray-500">
          Aucune tâche en attente. Bravo !
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activeGroups.map(([groupKey, groupItems]) => (
        <div
          key={groupKey}
          className={`rounded-lg border overflow-hidden ${TIMELINE_GROUPS[groupKey].borderColor}`}
        >
          <TimelineGroupHeader group={groupKey} count={groupItems.length} />
          <div className="divide-y divide-gray-100 bg-white">
            {groupItems.map(item => (
              <WorkItemRow
                key={item.id}
                item={item}
                onAction={onAction}
                onNavigate={onNavigate}
                onEmail={onEmail}
                onSuggestedAction={onSuggestedAction}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default WorkItemTimeline;
